import { useRef, useState } from "react";
import { Form, Link, redirect, useActionData, useFetcher, useNavigation, useParams } from "react-router";
import type { ActionFunctionArgs, LoaderFunctionArgs } from "react-router";
import { useQuery, useSuspenseQueries } from "@tanstack/react-query";
import type { SpaceEvent, Team, TeamMember } from "@repo/shared-types";
import { api } from "@/lib/api";
import { errorMessage } from "@/lib/session";
import { loadSpaceContext, useSpaceContext } from "@/lib/events";
import { heldBy, noneWhenMissing, queries } from "@/lib/queries";
import { read } from "@/lib/query";
import { DIVISIONS, LEVELS, MAX_NOTE, eventParts, levelLabel, readTeamScore, type RecordablePart } from "@/lib/results";
import {
  MAX_GUEST_NAME,
  memberAsPerson,
  memberName,
  onTeam,
  readTeamPeople,
  teamDivisionLabel,
} from "@/lib/teams";
import { tenantPath } from "@/lib/tenants";
import { formatMoment } from "@/lib/times";
import { toast } from "@/lib/toast";
import { UsersRound } from "lucide-react";
import { ConfirmDialog, Modal, useClosedWhenDone, type Outcome } from "@/components/modal";
import { ErrorNote, Field, ProblemNote, ProblemProvider, SegmentedControl, useFormProblem } from "@/components/form";
import { FeelAndNote } from "@/components/feel-and-note";
import { Feel, RxPlusToggle, ScoreBoxes } from "@/components/result-fields";
import { TeamPeopleBox, TeamStanding, teamScore } from "@/components/team-fields";
import { Conversation } from "@/components/conversation";
import { Avatar } from "@/components/avatar";
import { BackLink, useAccountLink } from "@/components/navigation";
import { PageTitle } from "@/components/brand";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Item, ItemActions, ItemContent, ItemDescription, ItemMedia, ItemTitle } from "@/components/ui/item";

/**
 * One team's score on an event's team part (0027): its score and where it
 * stands, its people and what each is to it, the proposal open on it, the
 * caller's own note, and its conversation.
 *
 * What the caller may do follows what they are to it, which the api says:
 * an invitee accepts or declines — accepting registers them for the event,
 * privately, if they had no result, and then asks whether to share it, which
 * puts their name on the team's line, and it is refused while they are on
 * another team for the part (0036: one team a part); its creator changes its
 * score and its people and answers proposals; a teammate proposes a change, keeps their own
 * note, and leaves. Its space reads its line, and talks about it, while one
 * of its people shares; hidden by a moderator, it is its people's alone.
 *
 * A save says it worked by a toast where its effect is not where the person
 * is — a note, a proposal and its answers, the score changed from its sheet,
 * a guest renamed — and says nothing where it shows on the page, as people
 * added or taken off do. Taking somebody off, leaving and hiding ask first
 * (decided with Tiago 12 October).
 */

export async function teamLoader({ request, params }: LoaderFunctionArgs) {
  const slug = params.slug ?? "";
  const eventId = params.event ?? "";
  const teamId = params.team ?? "";
  await Promise.all([
    loadSpaceContext(request, slug),
    read(queries.event(slug, eventId)),
    read(queries.team(slug, eventId, teamId)),
    read(queries.teamThread(slug, eventId, teamId)).catch(noneWhenMissing),
    read(queries.myTeams(slug, eventId)),
    read(queries.myEntry(slug, eventId)),
  ]);
  return null;
}

type ActionResult = { error?: string; field?: string; ok?: string; askShare?: boolean };

/** A save that worked, said by a toast: its effect is not where the person is. */
function said(message: string): ActionResult {
  toast(message);
  return { ok: message };
}

/** The team's part, as its score form reads it. */
function partOf(event: SpaceEvent, team: Team): RecordablePart | undefined {
  return eventParts(event, null).find((part) => part.id === team.partId);
}

export async function teamAction({ request, params }: ActionFunctionArgs): Promise<ActionResult | Response> {
  const form = await request.formData();
  const slug = params.slug ?? "";
  const eventId = params.event ?? "";
  const teamId = params.team ?? "";
  const events = api.space(slug).events;
  const teams = events.teams;
  const get = (name: string) => String(form.get(name) ?? "");
  const eventPath = `/t/${slug}/events/${eventId}`;

  try {
    switch (get("intent")) {
      case "accept": {
        // No division: the api reads the profile's, and refuses without one.
        await teams.accept(eventId, teamId, {});
        const entry = await events.entry.get(eventId).catch(noneWhenMissing);
        return entry?.shared ? said("You're on the team.") : { askShare: true };
      }
      // Both land on the event's page, where nothing shows what happened.
      case "decline":
        await teams.decline(eventId, teamId);
        toast("Invitation declined.");
        return redirect(eventPath);
      case "leave":
        await teams.leave(eventId, teamId);
        toast("You left the team.");
        return redirect(eventPath);
      case "share":
        await events.entry.share(eventId);
        return said("Your result is on the leaderboard, and your name on the team's line.");

      case "change":
      case "propose": {
        const [event, team] = await Promise.all([events.get(eventId), teams.get(eventId, teamId)]);
        const part = partOf(event, team);
        if (!part) return { error: "This team's part is gone." };
        const read = readTeamScore(form, part);
        if (read.error !== undefined) return { error: read.error, field: read.field };
        const body = {
          ...(read.value.level ? { level: read.value.level } : {}),
          ...(read.value.rxPlus !== null ? { rxPlus: read.value.rxPlus } : {}),
          scores: read.value.scores,
        };
        if (get("intent") === "change") {
          await teams.change(eventId, teamId, body);
          return said("The team's score is changed, and your teammates told.");
        }
        await teams.propose(eventId, teamId, body);
        return said("Proposed. The team's creator is told, and answers it here.");
      }
      case "withdraw-proposal":
        await teams.withdrawProposal(eventId, teamId, get("proposal"));
        return said("Proposal withdrawn.");
      // Approved or turned down, the proposal goes the same way: the words say which.
      case "approve":
        await teams.approve(eventId, teamId, get("proposal"));
        return said("Approved: the team's score is changed.");
      case "reject":
        await teams.reject(eventId, teamId, get("proposal"));
        return said("Turned down. The score stays as it was.");

      // Who is added or taken off shows in People at once: nothing to say.
      case "add-people": {
        const read = readTeamPeople(form, "add", "The team");
        if (read.error !== undefined) return { error: read.error, field: read.field };
        if (read.value.invite.length > 0) await teams.invite(eventId, teamId, { usernames: read.value.invite });
        if (read.value.guests.length > 0) await teams.addGuests(eventId, teamId, { guests: read.value.guests });
        return { ok: "Added." };
      }
      case "withdraw-invitation":
        await teams.withdrawInvitation(eventId, teamId, get("user"));
        return { ok: "Invitation withdrawn." };
      case "remove-guest":
        await teams.removeGuest(eventId, teamId, get("member"));
        return { ok: "Guest removed." };
      case "change-guest": {
        const name = get("name").trim();
        if (!name) return { error: "A guest has a name.", field: "name" };
        await teams.changeGuest(eventId, teamId, get("member"), { name, division: get("division") });
        return said("Guest changed.");
      }

      case "note": {
        const note = get("note").trim();
        if (note.length > MAX_NOTE) return { error: `A note is at most ${MAX_NOTE} characters.` };
        await teams.note(teamId, { ...(note ? { note } : {}), ...(get("feel") ? { feel: get("feel") } : {}) });
        return said("Saved your note.");
      }

      // Where the team stands shows under its title, not beside the button.
      case "verify":
        await teams.verify(eventId, teamId, { changedAt: get("changedAt") });
        return said("Verified.");
      case "unverify":
        await teams.unverify(eventId, teamId);
        return said("Verification taken back.");
      case "hide":
        await teams.hide(eventId, teamId);
        return redirect(`${eventPath}/hidden`);

      default:
        return { error: "Unknown action" };
    }
  } catch (error) {
    return { error: errorMessage(error, "Something went wrong") };
  }
}

export default function TeamPage() {
  const { slug = "", event: eventId = "", team: teamId = "" } = useParams();
  const { tenant, session, profile, zone, canVerify, canModerate } = useSpaceContext(slug);
  const [{ data: event }, { data: team }, { data: mine }, { data: entry }] = useSuspenseQueries({
    queries: [
      queries.event(slug, eventId),
      queries.team(slug, eventId, teamId),
      queries.myTeams(slug, eventId),
      queries.myEntry(slug, eventId),
    ],
  });
  // A 404 is an answer, and a loader that got one left it as it was: not asked again on mounting.
  const thread = heldBy(useQuery({ ...queries.teamThread(slug, eventId, teamId), retryOnMount: false }));
  // The team the caller is on for this part already, if another: joining this one waits for leaving it.
  const onAnother = mine.teams.find((other) => other.id !== team.id && other.partId === team.partId && onTeam(other)) ?? null;
  // In an event ranked overall a team is at its people's one level (0037):
  // fixed, and joined only at it.
  const fixedLevel = event.ranking !== "none" && team.level !== null;
  const otherLevel = fixedLevel && entry?.level && entry.level !== team.level ? entry.level : null;
  const result = useActionData() as ActionResult | undefined;
  const navigation = useNavigation();
  const busy = navigation.state === "submitting";
  const [keptPrivate, setKeptPrivate] = useState<ActionResult | undefined>(undefined);
  const eventPath = `${tenantPath(tenant)}/events/${event.id}`;
  const part = partOf(event, team);
  const me = team.members.find((member) => member.user?.id === session.user.id);
  const onIt = onTeam(team);

  return (
    <section className="space-y-6">
      <div className="space-y-2">
        <BackLink to={eventPath}>{event.title}</BackLink>
        <PageTitle of={[part ? `${part.title}, a team` : "A team", event.title]} />
        <h1 className="page-title">{part ? `${part.title}, a team` : "A team"}</h1>
        <p className="text-muted-foreground">
          <span className="font-medium text-foreground">{teamScore(team)}</span>
          {team.division && ` · ${teamDivisionLabel(team.division)}`}
          {` · ${team.people} ${team.people === 1 ? "person" : "people"}`}
        </p>
        <TeamStanding team={team} spaceName={tenant.name} />
      </div>

      {/* Joining and sharing are answered here, at the top, where they are asked. */}
      {result?.error && <ErrorNote message={result.error} />}
      {result?.askShare && keptPrivate !== result && (
        <Alert>
          <UsersRound />
          <AlertTitle>You're on the team.</AlertTitle>
          <AlertDescription>
            Your result for {event.title} is private: show it on {tenant.name}'s leaderboard? Your name goes on the
            team's line once you do.
          </AlertDescription>
          <div className="mt-3 flex flex-wrap gap-2 group-has-[>svg]/alert:col-start-2">
            <Form method="post">
              <input type="hidden" name="intent" value="share" />
              <Button type="submit" disabled={busy}>
                Share
              </Button>
            </Form>
            <Button type="button" variant="outline" onClick={() => setKeptPrivate(result)}>
              Keep private
            </Button>
          </div>
        </Alert>
      )}

      {team.role === "invited" && (
        <Invitation
          hasDivision={profile.division !== null}
          onAnother={onAnother && `${eventPath}/teams/${onAnother.id}`}
          levels={otherLevel && team.level ? { yours: otherLevel, team: team.level } : null}
          busy={busy}
        />
      )}

      <People team={team} meId={session.user.id} />
      {team.role === "creator" && <AddPeople team={team} slug={tenant.slug} />}

      {part && onIt && (
        <Score team={team} part={part} fixedLevel={fixedLevel} meId={session.user.id} />
      )}

      {onIt && me && <OwnNotes member={me} />}

      {(canVerify || canModerate) && team.standing !== null && team.recorded && (
        <SpaceTools team={team} canVerify={canVerify} canModerate={canModerate} />
      )}

      {onIt && <LeaveTeam creator={team.role === "creator"} />}

      {/* The conversation draws its own heading: none of the page's above it. */}
      <Conversation
        key={team.id}
        target={{ slug: tenant.slug, eventId: event.id, entryId: null, teamId: team.id }}
        page={thread ?? { comments: [], nextCursor: null }}
        reactions={team.reactions}
        writes
        closed={thread ? null : "The team's conversation opens once one of you shares your result."}
      />

      <p className="text-xs text-muted-foreground">Formed {formatMoment(team.createdAt, zone)}.</p>
    </section>
  );
}

/**
 * An invitation to the team: accept or decline. Joining takes the profile's
 * division — a team's is worked out from its people's — so without one it
 * says where to set it; and it waits while the caller is on another team for
 * the part, which it links to. Declining is always there.
 */
function Invitation({
  hasDivision,
  onAnother,
  levels,
  busy,
}: {
  hasDivision: boolean;
  /** The other team's page, when the caller is on one for this part. */
  onAnother: string | null;
  /** In an event ranked overall, the caller's level and the team's, when they differ (0037). */
  levels: { yours: string; team: string } | null;
  busy: boolean;
}) {
  const setDivision = useAccountLink("division");
  return (
    <section aria-label="Your invitation">
      <Card className="ring-info/30 dark:ring-info/40">
        <CardContent className="space-y-4">
          <p className="text-sm text-foreground">
            You're invited to this team. Join it, and it is in your log with your own note on it; nothing is on the
            leaderboard with your name until you share your result.
          </p>
          <div className="flex flex-wrap gap-2">
            <Form method="post">
              <input type="hidden" name="intent" value="accept" />
              <Button
                type="submit"
                size="lg"
                disabled={busy || !hasDivision || onAnother !== null || levels !== null}
              >
                Join the team
              </Button>
            </Form>
            <Form method="post">
              <input type="hidden" name="intent" value="decline" />
              <Button type="submit" variant="outline" size="lg" disabled={busy}>
                Decline
              </Button>
            </Form>
          </div>
          {levels && (
            <p className="text-sm text-warning">
              You're registered at {levelLabel(levels.yours)} for this event; this team is {levelLabel(levels.team)}.
            </p>
          )}
          {onAnother && (
            <p className="text-sm text-warning">
              You're on{" "}
              <Link to={onAnother} className="underline underline-offset-4">
                a team for this part
              </Link>{" "}
              — leave it to join this one.
            </p>
          )}
          {!hasDivision && (
            <p className="text-sm text-warning">
              Set your division in{" "}
              <Link to={setDivision} className="underline underline-offset-4">
                Account
              </Link>{" "}
              to join: a team's division is worked out from its people's.
            </p>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

function statusLabel(member: TeamMember): string {
  switch (member.status) {
    case "invited":
      return "invited";
    case "guest":
      return "guest";
    default:
      return member.shared ? "joined · on the leaderboard" : "joined · private";
  }
}

/** What the creator asked to do to one person on the roster: a question or a sheet each. */
type RosterAsk = { what: "withdraw" | "remove" | "change"; member: TeamMember };

/**
 * Who is on the team, what each is to it, and — for its creator — the
 * roster's controls. Taking somebody off asks first: a withdrawn invitation
 * goes from its invitee's inbox, and a guest taken off is typed again to
 * come back.
 */
function People({ team, meId }: { team: Team; meId: string }) {
  const creator = team.role === "creator";
  const unnamed = team.people - team.members.length;
  const [asking, setAsking] = useState<RosterAsk | null>(null);
  const close = () => setAsking(null);
  return (
    <section className="space-y-3" aria-label="People">
      <h2 className="section-label">People</h2>
      <Card className="gap-0 py-1.5">
        <ul className="divide-y divide-border">
          {team.members.map((member) => (
            <li key={member.id}>
              <Item className="rounded-none">
                <ItemMedia>
                  <Avatar person={memberAsPerson(member)} size="md" />
                </ItemMedia>
                <ItemContent className="min-w-0 gap-0.5">
                  <ItemTitle className="line-clamp-none block w-full wrap-anywhere">
                    {memberName(member)}
                    {member.user?.id === meId && <span className="font-normal text-muted-foreground"> — you</span>}
                    {member.user?.id === team.creatorId && (
                      <span className="font-normal text-muted-foreground"> · looks after the score</span>
                    )}
                  </ItemTitle>
                  <ItemDescription>
                    {statusLabel(member)}
                    {member.division && ` · ${member.division === "men" ? "Men" : "Women"}`}
                  </ItemDescription>
                  {member.user?.id !== meId && (member.note || member.feel) && (
                    <p className="text-sm text-foreground wrap-anywhere">
                      <Feel feel={member.feel} /> {member.note}
                    </p>
                  )}
                </ItemContent>
                {creator && member.status === "invited" && member.user && (
                  <ItemActions>
                    <Button type="button" variant="outline" size="sm" onClick={() => setAsking({ what: "withdraw", member })}>
                      Withdraw
                    </Button>
                  </ItemActions>
                )}
                {creator && member.status === "guest" && (
                  <ItemActions>
                    <Button type="button" variant="outline" size="sm" onClick={() => setAsking({ what: "change", member })}>
                      Change
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => setAsking({ what: "remove", member })}>
                      Remove
                    </Button>
                  </ItemActions>
                )}
              </Item>
            </li>
          ))}
        </ul>
        {unnamed > 0 && (
          <p className="border-t border-border px-4 pt-3 pb-1.5 text-sm text-muted-foreground">
            And {unnamed} {unnamed === 1 ? "other" : "others"}, named here once they share their result.
          </p>
        )}
      </Card>
      {asking?.what === "withdraw" && asking.member.user && (
        <ConfirmDialog
          title={`Withdraw ${memberName(asking.member)}'s invitation?`}
          fields={{ intent: "withdraw-invitation", user: asking.member.user.id }}
          confirm="Withdraw invitation"
          onClose={close}
        >
          It goes from their inbox too. You can invite them again.
        </ConfirmDialog>
      )}
      {asking?.what === "remove" && (
        <ConfirmDialog
          title={`Remove ${memberName(asking.member)} from the team?`}
          fields={{ intent: "remove-guest", member: asking.member.id }}
          confirm="Remove guest"
          onClose={close}
        >
          The team's division is worked out again from whoever is left. You can add them again.
        </ConfirmDialog>
      )}
      {asking?.what === "change" && <GuestDialog member={asking.member} onClose={close} />}
    </section>
  );
}

/** A guest's name, and men or women, changed in a sheet by the team's creator. */
function GuestDialog({ member, onClose }: { member: TeamMember; onClose: () => void }) {
  const fetcher = useFetcher<Outcome>();
  useClosedWhenDone(fetcher, onClose);
  const [division, setDivision] = useState<string>(member.division === "women" ? "women" : "men");
  const form = useRef<HTMLFormElement>(null);
  const shown = useFormProblem(fetcher.data, form);
  return (
    <Modal title={`Change ${memberName(member)}`} onClose={onClose}>
      <fetcher.Form ref={form} method="post" className="space-y-4" noValidate onChange={shown.onChange}>
        <input type="hidden" name="intent" value="change-guest" />
        <input type="hidden" name="member" value={member.id} />
        <Field
          label="Name"
          name="name"
          defaultValue={member.guestName ?? ""}
          maxLength={MAX_GUEST_NAME}
          error={shown.problem?.field === "name" ? shown.problem.error : undefined}
        />
        <SegmentedControl
          label="Division"
          name="division"
          options={DIVISIONS.map((option) => ({ ...option }))}
          value={division}
          onChange={setDivision}
        />
        {shown.general && <ErrorNote message={shown.general} />}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" size="lg" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" size="lg" disabled={fetcher.state !== "idle"}>
            Save
          </Button>
        </div>
      </fetcher.Form>
    </Modal>
  );
}

/**
 * The creator adds people: members invited by @username, guests by name.
 * Each shows in People once added; a refusal shows here, beside the box.
 */
function AddPeople({ team, slug }: { team: Team; slug: string }) {
  const fetcher = useFetcher<Outcome>();
  const form = useRef<HTMLFormElement>(null);
  const shown = useFormProblem(fetcher.data, form);
  return (
    <fetcher.Form ref={form} method="post" className="space-y-3" aria-label="Add people" onChange={shown.onChange}>
      <input type="hidden" name="intent" value="add-people" />
      <h2 className="section-label">Add people</h2>
      {/* Not clipped, so the members typing @ suggests may hang past its edge. */}
      <Card className="overflow-visible">
        <CardContent className="space-y-4">
          {/* Keyed by how many the team holds, so the box starts empty once they are added. */}
          <ProblemProvider value={shown.problem}>
            <TeamPeopleBox key={team.people} prefix="add" slug={slug} heading={null} />
            <ProblemNote name="add.team" />
          </ProblemProvider>
          {shown.general && <ErrorNote message={shown.general} />}
          <Button type="submit" variant="outline" size="lg" disabled={fetcher.state !== "idle"}>
            Add to the team
          </Button>
        </CardContent>
      </Card>
    </fetcher.Form>
  );
}

/**
 * The score's controls: its creator changes it; a teammate proposes a
 * change, one open at a time — each in a sheet, as a part's result is
 * recorded on the event's page. The open proposal shows against the score as
 * it is now, with its answers.
 */
function Score({
  team,
  part,
  fixedLevel,
  meId,
}: {
  team: Team;
  part: RecordablePart;
  /** In an event ranked overall the team's level is its people's, fixed (0037). */
  fixedLevel: boolean;
  meId: string;
}) {
  const proposal = team.proposal;
  const creator = team.role === "creator";
  const mine = proposal !== null && proposal.proposer.id === meId;
  // One fetcher for the proposal's answers, so a refusal shows here rather than at the top.
  const answer = useFetcher<Outcome>();
  const answering = answer.state !== "idle";
  const [changing, setChanging] = useState(false);
  return (
    <section className="space-y-3" aria-label="The team's score">
      <h2 className="section-label">The team's score</h2>
      <Card>
        <CardContent className="space-y-4">
          {proposal && (
            <div className="space-y-3 rounded-2xl bg-warning-muted p-3 text-sm text-foreground">
              <p className="wrap-anywhere">
                <Avatar person={proposal.proposer} size="xs" className="mr-1.5" />
                <span className="font-medium">@{proposal.proposer.username}</span> proposes{" "}
                <strong className="font-semibold">
                  {teamScore({ ...team, recorded: true, scores: proposal.scores, level: proposal.level, rxPlus: proposal.rxPlus })}
                </strong>{" "}
                — it is {teamScore(team)} now.
              </p>
              <div className="flex flex-wrap gap-2">
                {creator && (
                  <>
                    <answer.Form method="post">
                      <input type="hidden" name="intent" value="approve" />
                      <input type="hidden" name="proposal" value={proposal.id} />
                      <Button type="submit" size="sm" disabled={answering}>
                        Approve
                      </Button>
                    </answer.Form>
                    <answer.Form method="post">
                      <input type="hidden" name="intent" value="reject" />
                      <input type="hidden" name="proposal" value={proposal.id} />
                      <Button type="submit" variant="outline" size="sm" disabled={answering}>
                        Turn down
                      </Button>
                    </answer.Form>
                  </>
                )}
                {mine && (
                  <answer.Form method="post">
                    <input type="hidden" name="intent" value="withdraw-proposal" />
                    <input type="hidden" name="proposal" value={proposal.id} />
                    <Button type="submit" variant="outline" size="sm" disabled={answering}>
                      Withdraw
                    </Button>
                  </answer.Form>
                )}
              </div>
            </div>
          )}
          {answer.data?.error && <ErrorNote message={answer.data.error} />}
          {creator || !proposal ? (
            <Button type="button" variant="outline" size="lg" onClick={() => setChanging(true)}>
              {creator ? "Change the score" : "Propose a change"}
            </Button>
          ) : (
            <p className="text-sm text-muted-foreground">A proposal is open: propose another once it is answered.</p>
          )}
        </CardContent>
      </Card>
      {changing && (
        <ScoreDialog
          team={team}
          part={part}
          fixedLevel={fixedLevel}
          intent={creator ? "change" : "propose"}
          onClose={() => setChanging(false)}
        />
      )}
    </section>
  );
}

type Level = (typeof LEVELS)[number]["value"];

/** The team's score typed in a sheet, starting from the score as it is: its creator's change, or a teammate's proposal. */
function ScoreDialog({
  team,
  part,
  fixedLevel,
  intent,
  onClose,
}: {
  team: Team;
  part: RecordablePart;
  fixedLevel: boolean;
  intent: "change" | "propose";
  onClose: () => void;
}) {
  const fetcher = useFetcher<Outcome>();
  useClosedWhenDone(fetcher, onClose);
  const [level, setLevel] = useState<string>(team.level ?? "rx");
  const form = useRef<HTMLFormElement>(null);
  const shown = useFormProblem(fetcher.data, form);
  return (
    <Modal title={intent === "change" ? "Change the score" : "Propose a change"} onClose={onClose}>
      <p className="text-sm text-muted-foreground">{part.summary}</p>
      <fetcher.Form ref={form} method="post" className="space-y-4" noValidate onChange={shown.onChange}>
        <input type="hidden" name="intent" value={intent} />
        <ProblemProvider value={shown.problem}>
          {part.types.map((type, position) => (
            <div key={position} className="space-y-1.5">
              {part.types.length > 1 && (
                <p className="text-sm text-muted-foreground">{position === 0 ? type.label : `Then ${type.label.toLowerCase()} (optional)`}</p>
              )}
              <ScoreBoxes
                prefix={`scores.${position}`}
                type={type}
                position={position}
                timeLimitMs={part.timeLimitMs}
                current={team.scores[position]}
              />
            </div>
          ))}
          {part.usesLevels && (
            <div className="space-y-3 text-sm">
              {fixedLevel ? (
                <p className="text-muted-foreground">
                  <input type="hidden" name="level" value={level} />
                  {levelLabel(level)}, the team's level for the event: its people's, fixed.
                </p>
              ) : (
                <SegmentedControl<Level>
                  label="Level"
                  name="level"
                  options={LEVELS.map((option) => ({ ...option }))}
                  value={LEVELS.some((option) => option.value === level) ? (level as Level) : ""}
                  onChange={setLevel}
                />
              )}
              {level === "rx" && <RxPlusToggle name="rxPlus" on={team.rxPlus} />}
            </div>
          )}
        </ProblemProvider>
        {shown.general && <ErrorNote message={shown.general} />}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" size="lg" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" size="lg" disabled={fetcher.state !== "idle"}>
            {intent === "change" ? "Save the score" : "Propose it"}
          </Button>
        </div>
      </fetcher.Form>
    </Modal>
  );
}

/**
 * The caller's own record of the team: a feel and a note, theirs to share as
 * any result's, in the one look they have everywhere.
 */
function OwnNotes({ member }: { member: TeamMember }) {
  const fetcher = useFetcher<Outcome>();
  return (
    <fetcher.Form method="post" className="space-y-3" aria-label="Your own notes">
      <input type="hidden" name="intent" value="note" />
      <h2 className="section-label">Your own notes</h2>
      <Card>
        <CardContent className="space-y-4">
          <FeelAndNote feelName="feel" noteName="note" feelLabel="Feel" feel={member.feel} note={member.note} />
          <p className="text-sm text-muted-foreground">
            Yours alone, unless you share your result with your notes — the same toggle as your other results.
          </p>
          {fetcher.data?.error && <ErrorNote message={fetcher.data.error} />}
          <Button type="submit" variant="outline" size="lg" disabled={fetcher.state !== "idle"}>
            Save note
          </Button>
        </CardContent>
      </Card>
    </fetcher.Form>
  );
}

/**
 * What the space's verifiers and moderators do to the team's score from its
 * page. Hiding asks first, in the words its board line uses.
 */
function SpaceTools({ team, canVerify, canModerate }: { team: Team; canVerify: boolean; canModerate: boolean }) {
  const fetcher = useFetcher<Outcome>();
  const [hiding, setHiding] = useState(false);
  const verified = team.verification?.current ?? false;
  return (
    <section className="space-y-2" aria-label="The space's tools">
      <div className="flex flex-wrap gap-2">
        {canVerify && (
          <fetcher.Form method="post">
            <input type="hidden" name="intent" value={verified ? "unverify" : "verify"} />
            {team.changedAt && <input type="hidden" name="changedAt" value={team.changedAt} />}
            <Button type="submit" variant="outline" size="lg" disabled={fetcher.state !== "idle"}>
              {verified ? "Take back the verification" : "Verify"}
            </Button>
          </fetcher.Form>
        )}
        {canModerate && (
          <Button type="button" variant="outline" size="lg" onClick={() => setHiding(true)}>
            Hide from the leaderboard…
          </Button>
        )}
      </div>
      {fetcher.data?.error && <ErrorNote message={fetcher.data.error} />}
      {hiding && (
        <ConfirmDialog
          title="Hide this team on this part's board?"
          fields={{ intent: "hide" }}
          confirm="Hide"
          onClose={() => setHiding(false)}
        >
          It stays in its people's logs, and those who shared are told the space hid it. Put it back from the event's
          hidden results.
        </ConfirmDialog>
      )}
    </section>
  );
}

/** Leaving the team, asked first: it goes from the caller's log. */
function LeaveTeam({ creator }: { creator: boolean }) {
  const [asking, setAsking] = useState(false);
  return (
    <div>
      <Button type="button" variant="outline" size="lg" onClick={() => setAsking(true)}>
        Leave the team…
      </Button>
      {asking && (
        <ConfirmDialog title="Leave the team?" fields={{ intent: "leave" }} confirm="Leave the team" onClose={() => setAsking(false)}>
          {creator ? "Looking after its score passes to whoever joined next. " : ""}
          It leaves your log; the last of you to leave takes the team with them.
        </ConfirmDialog>
      )}
    </div>
  );
}
